import React, { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Group, Title, Select, Text, NumberInput, Box } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { getLevels, getChallengeTypes, saveChallenge, parseToken, updateChallenge } from '../../services/api.service';
import { DateTimePicker } from '@mantine/dates';

const ChallengeForm = ({ closeModal, isEdit, challenge }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [levels, setLevels] = useState([]);
	const [challengeTypes, setChallengeTypes] = useState([]);
	const [tokenData, setTokenData] = useState(null);
	const [amountText, setAmountText] = useState('');
	const [amountDescription, setAmountDescription] = useState('');
	const form = useForm({
		initialValues: {
			name: '',
			amount: '',
			points: '',
			description: '',
			startDate: null,
			endDate: null,
			challengeType: {
				id: '',
			},
			level: {
				id: 0,
			},
		},
	});

	// Check if edit
	useEffect(() => {
		if (isEdit) form.setValues({
			name: challenge.name,
			amount: challenge.amount,
			points: challenge.points,
			description: challenge.description,
			startDate: challenge.startDate ? new Date(challenge.startDate) : null,
			endDate: challenge.endDate ? new Date(challenge.endDate) : null,
			challengeType: {
				id: `${translateChallengeType(challenge?.challengeType?.name)}:::${challenge?.challengeType?.id}`
			},
			level: {
				id: challenge.level?.id,
			},
		});
		// eslint-disable-next-line
	}, []);

	// Listen to challenge type changes
	useEffect(() => {

		// Delete text
		if (form.values.challengeType?.id?.split(':::')[0] === '') setAmountText('');

		// Set text
		setAmountText(form.values.challengeType?.id?.split(':::')[0] === 'Actividad' ?
			'de kilometros a recorrer'
		: form.values.challengeType?.id?.split(':::')[0] === 'Beneficio' ?
			'de beneficios a canjear'
		: form.values.challengeType?.id?.split(':::')[0] === 'Referido' ?
			'de amigos a referir'
		: '');

		// Set description
		setAmountDescription(form.values.challengeType?.id?.split(':::')[0] === 'Actividad' ?
			'Elegir cantidad de kilometros a recorrer para completar el desafío.'
		: form.values.challengeType?.id?.split(':::')[0] === 'Beneficio' ?
			'Elegir cantidad de beneficios a canjear para completar el desafío.'
		: form.values.challengeType?.id?.split(':::')[0] === 'Referido' ?
			'Elegir cantidad de amigos referidos para compeltar el desafío.'
		: '');

	}, [form.values.challengeType.id]);

	// On load
	useEffect(() => {

		// Parse token data
		setTokenData(parseToken(localStorage.getItem('webikecc_jwt')));

		// Get levels
		getLevels(function (response) {

			// Loop levels
			var levels = [];
			for (var i = 0; i < response.data.length; i++) {

				// Assign
				levels.push({
					label: response.data[i].name,
					value: response.data[i].id,
				});

			}

			// Set state
			setLevels(levels);

		}, function (error) {

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

		// Get challenge types
		getChallengeTypes(function (response) {

			// Loop challenge types
			var challengeTypes = [];
			for (var i = 0; i < response.data.length; i++) {

				// Assign
				challengeTypes.push({
					label: translateChallengeType(response.data[i].name),
					value: `${translateChallengeType(response.data[i].name)}:::${response.data[i].id}`,
				});

			}

			// Set state
			setChallengeTypes(challengeTypes);

		}, function (error) {

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}, []);

	// Translate challenge type
	const translateChallengeType = (type) => {

		// Assign
		let translatedType = '';
		switch (type) {
			case 'Activity':
				translatedType = 'Actividad';
				break;
			case 'Benefit':
				translatedType = 'Beneficio';
				break;
			case 'Profile':
				translatedType = 'Perfil';
				break;
			case 'Referal':
				translatedType = 'Referido';
				break;
			default:
				translatedType = type;
				break;
		}

		// Return
		return translatedType;

	}

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Build object for API
			const formValues = {
				name: values.name,
				amount: values.amount,
				points: values.points,
				description: values.description,
				startDate: values.startDate,
				endDate: values.endDate,
				challengeType: {
					id: Number(values.challengeType?.id?.split(':::')[1]),
				},
				level: {
					id: values.level.id,
				}
			};

			// Call API
			saveChallenge(formValues, function () {

				// Show success
				notifications.show({
					title: 'Desafío creado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Build object for API
			const formValues = {
				name: values.name,
				amount: values.amount,
				points: values.points,
				description: values.description,
				startDate: values.startDate,
				endDate: values.endDate,
				challengeType: {
					id: Number(values.challengeType?.id?.split(':::')[1]),
				},
				level: {
					id: values.level.id,
				}
			};

			// Call API
			updateChallenge(challenge.id, formValues, function () {

				// Show success
				notifications.show({
					title: 'Desafío modificado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	return (
		<>
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información del Desafío
				</Title>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Name */}
					<TextInput
						required
						label="Nombre"
						placeholder="Nombre del desafío"
						value={form.values.name}
						onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
						{...form.getInputProps('name')}
						maxLength={65}
					/>
					{/* Description */}
					<TextInput
						required
						label="Descripción"
						placeholder="Descripción del desafío"
						value={form.values.description}
						onChange={(event) => form.setFieldValue('description', event.currentTarget.value)}
						{...form.getInputProps('description')}
						maxLength={130}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Points */}
					<NumberInput
						placeholder="Puntos por completar"
						label="Puntos"
						required
						min={1}
						value={form.values.points}
						onChange={(event) => form.setFieldValue('points', event.currentTarget.value)}
						{...form.getInputProps('points')}
					/>
					{/* Level ID */}
					<Select
						searchable
						withinPortal
						required
						label="Nivel"
						placeholder="Busca un nivel"
						value={form.values.level.id}
						onChange={(event) => form.setFieldValue('level.id', event.currentTarget.value)}
						{...form.getInputProps('level.id')}
						nothingFound="Sin resultados"
						data={levels}
						rightSection={<IconChevronDown size="1rem" />}
      					rightSectionWidth={30}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
					align="flex-start"
				>
					{/* Challenge Type ID */}
					<Select
						searchable
						withinPortal
						required
						label="Tipo de Desafío"
						placeholder="Busca un tipo de desafío"
						value={form.values.challengeType.id}
						onChange={(event) => form.setFieldValue('challengeType.id', event.currentTarget.value)}
						{...form.getInputProps('challengeType.id')}
						nothingFound="Sin resultados"
						data={challengeTypes}
						rightSection={<IconChevronDown size="1rem" />}
      					rightSectionWidth={30}
					/>
					{/* Amount */}
					<Box opacity={form.values.challengeType?.id?.split(':::')[0] === '' || form.values.challengeType?.id?.split(':::')[0] === 'Perfil' ? 0.3 : 1}>
						{/* Input */}
						<NumberInput
							placeholder="Cantidad para completar"
							label={`Cantidad ${amountText}`}
							required={form.values.challengeType?.id?.split(':::')[0] === 'Actividad' || form.values.challengeType?.id?.split(':::')[0] === 'Beneficio' || form.values.challengeType?.id?.split(':::')[0] === 'Referido'}
							min={1}
							value={form.values.amount}
							onChange={(event) => form.setFieldValue('amount', event.currentTarget.value)}
							{...form.getInputProps('amount')}
							disabled={form.values.challengeType?.id?.split(':::')[0] === '' || form.values.challengeType?.id?.split(':::')[0] === 'Perfil'}
						/>
						{/* Description */}
						<Text
							c="dimmed"
							mt={4}
							ta="right"
							lh="16px"
							fz={10}
						>
							{amountDescription}
						</Text>
					</Box>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="md"
				>
					{/* Start Date */}
					<DateTimePicker
						required
						label="Fecha de Inicio"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.startDate}
						onChange={(event) => form.setFieldValue('startDate', event.currentTarget.value)}
						{...form.getInputProps('startDate')}
						dropdownType="modal"
						modalProps={{
							withinPortal: true,
							centered: true
						}}
					/>
					{/* End Date */}
					<DateTimePicker
						required
						label="Fecha de Finalización"
						placeholder="Elige una fecha"
						valueFormat="DD-MM-YYYY HH:mm"
						value={form.values.endDate}
						minDate={form.values.startDate}
						onChange={(event) => form.setFieldValue('endDate', event.currentTarget.value)}
						{...form.getInputProps('endDate')}
						dropdownType="modal"
						modalProps={{
							withinPortal: true,
							centered: true
						}}
					/>
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Desafío'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default ChallengeForm;
